import { USER_CREATED_PAYMENT_CLASS } from '@src/types/constants'
import { CustomerPaymentOperation, OperationInformation } from '@src/types/loan/loan-operation'
import { memo, useCallback } from 'react'
import { ITableColumn } from './LoanCalendarTable-hook'
import styles from './LoanCalendarTable.module.css'

interface LoanCalendarTableBodyProps {
  rows: OperationInformation<CustomerPaymentOperation>[]
  columns: ITableColumn[]
  visibleColumns: Record<string, boolean>
  selectedPaymentIds: Set<string>
  onSelectionChange: (ids: Set<string>) => void
  getRowClassName: (row: OperationInformation<CustomerPaymentOperation>, index: number) => string
}

const LoanCalendarTableBodyRow = memo(
  ({
    row,
    columns,
    visibleColumns,
    isSelected,
    onSelectionChange,
    getRowClassName,
    rowIndex,
  }: {
    row: OperationInformation<CustomerPaymentOperation>
    columns: ITableColumn[]
    visibleColumns: Record<string, boolean>
    isSelected: boolean
    onSelectionChange: (id: string) => void
    getRowClassName: (row: OperationInformation<CustomerPaymentOperation>, index: number) => string
    rowIndex: number
  }) => {
    const visibleColumnsArray = columns.filter((col) => visibleColumns[col.field] !== false)
    const rowClassNames = getRowClassName(row, rowIndex)
    const customClasses = rowClassNames.split(' ')

    let classesString = `${styles.table__row}`
    if (isSelected) classesString += ` ${styles['table__row--selected']}`
    if (!row.createdBySystem()) classesString += ` ${styles['table__row--user-created']}`

    // Add custom row styling classes
    customClasses.forEach((cls) => {
      if (cls === USER_CREATED_PAYMENT_CLASS) {
        classesString += ` ${styles['table__row--user-created']}`
      } else if (cls === 'row-after-last-payment-warning') {
        classesString += ` ${styles['table__row--after-last-payment-warning']}`
      } else if (cls === 'row-after-last-payment-info') {
        classesString += ` ${styles['table__row--after-last-payment-info']}`
      } else if (cls.startsWith('row-frequency-change-')) {
        classesString += ` ${styles['table__row--frequency-change']}`
      }
    })

    const handleCheckboxChange = useCallback(() => {
      onSelectionChange(row.simulatedOperationId)
    }, [row.simulatedOperationId, onSelectionChange])

    return (
      <tr className={classesString}>
        <td className={`${styles.table__cell} ${styles.table__cell__checkbox}`}>
          <input
            type="checkbox"
            className={styles.table__checkbox}
            checked={isSelected}
            onChange={handleCheckboxChange}
          />
        </td>
        {visibleColumnsArray.map((column) => {
          const cellAlign = column.align || 'left'
          const displayContent = column.render(row)

          return (
            <td
              key={column.field}
              className={`${styles.table__cell} ${
                cellAlign === 'center'
                  ? styles['table__cell--center']
                  : cellAlign === 'right'
                    ? styles['table__cell--right']
                    : ''
              } ${column.cellClassName === 'strong' ? styles['table__cell--strong'] : ''} ${
                column.cellClassName === 'wrap' ? styles['table__cell--wrap'] : ''
              } ${column.field === 'description' ? styles['table__cell--description'] : ''}`}
              style={{ width: column.width ? `${column.width}px` : column.field === 'description' ? 'auto' : undefined }}
            >
              {column.field === 'action' ? (
                <div className={styles.table__actions}>{displayContent}</div>
              ) : column.cellClassName === 'strong' || column.cellClassName === 'wrap' ? (
                <div className={column.cellClassName === 'strong' ? styles.table__status_icon : ''}>{displayContent}</div>
              ) : (
                displayContent
              )}
            </td>
          )
        })}
      </tr>
    )
  },
)

LoanCalendarTableBodyRow.displayName = 'LoanCalendarTableBodyRow'

const LoanCalendarTableBody = ({
  rows,
  columns,
  visibleColumns,
  selectedPaymentIds,
  onSelectionChange,
  getRowClassName,
}: LoanCalendarTableBodyProps) => {
  const handleSelectionChange = useCallback(
    (id: string) => {
      const newSelection = new Set(selectedPaymentIds)
      if (newSelection.has(id)) {
        newSelection.delete(id)
      } else {
        newSelection.add(id)
      }
      onSelectionChange(newSelection)
    },
    [selectedPaymentIds, onSelectionChange],
  )

  const visibleColumnsArray = columns.filter((col) => visibleColumns[col.field] !== false)

  if (rows.length === 0) {
    return (
      <tbody className={styles.table__body}>
        <tr>
          <td colSpan={visibleColumnsArray.length + 1} className={styles['table--empty']}>
            No data
          </td>
        </tr>
      </tbody>
    )
  }

  return (
    <tbody className={styles.table__body}>
      {rows.map((row, index) => (
        <LoanCalendarTableBodyRow
          key={row.simulatedOperationId}
          row={row}
          columns={columns}
          visibleColumns={visibleColumns}
          isSelected={selectedPaymentIds.has(row.simulatedOperationId)}
          onSelectionChange={handleSelectionChange}
          getRowClassName={getRowClassName}
          rowIndex={index}
        />
      ))}
    </tbody>
  )
}

export default LoanCalendarTableBody
